import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { apiFetch } from '@/lib/operator-api';
import {
  MessageSquare,
  Calendar,
  CreditCard,
  Bell,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  ChevronDown,
  RotateCcw,
  Eye,
  Save,
  Plus,
  X,
  ExternalLink,
  Link2,
  GripVertical,
  Copy,
  Trash2,
  Filter,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// ============================================================================
// Types
// ============================================================================

interface TemplateInfo {
  slug: string;
  isCustom: boolean;
  isDefault: boolean;
  category: string | null;
}

interface TemplateDetail {
  slug: string;
  bodyText: string;
  headerType?: 'none' | 'text' | 'image' | 'video';
  headerValue?: string;
  footerText?: string;
  buttons?: Array<{ type: string; text: string; url?: string }>;
  listSections?: Array<{
    title: string;
    rows: Array<{ id: string; title: string; description?: string }>;
  }>;
}

// ============================================================================
// Constants
// ============================================================================

function getSlugLabel(slug: string, t: (key: string) => string): string {
  return t(`templates.slugLabels.${slug}`) || slug;
}

const SLUG_ICONS: Record<string, typeof MessageSquare> = {
  greeting: MessageSquare,
  welcome_menu: MessageSquare,
  service_selection: MessageSquare,
  date_selection: Calendar,
  time_selection: Calendar,
  confirmation: CheckCircle2,
  payment_instructions: CreditCard,
  success: CheckCircle2,
  reminder_24h: Bell,
  reminder_2h: Bell,
  cancellation: AlertCircle,
  reschedule: Calendar,
  help: HelpCircle,
  error_invalid_input: AlertCircle,
  error_system: AlertCircle,
  error_no_availability: AlertCircle,
};

const AVAILABLE_VARIABLES = [
  '{{business_name}}',
  '{{customer_name}}',
  '{{service_name}}',
  '{{date}}',
  '{{time}}',
  '{{price}}',
];

const BODY_MAX_LENGTH = 1024;
const HEADER_TEXT_MAX_LENGTH = 60;
const FOOTER_MAX_LENGTH = 60;
const BUTTON_TITLE_MAX = 25;
const MAX_QUICK_REPLIES = 3;
const MAX_URL_BUTTONS = 1;

interface QuickReply {
  id: string;
  title: string;
}

interface UrlButton {
  label: string;
  url: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '') || 'button';
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

function getHeaderTypeOptions(t: (key: string) => string) {
  return [
    { value: 'none', label: t('templates.headerTypes.none') },
    { value: 'text', label: t('templates.headerTypes.text') },
    { value: 'image', label: t('templates.headerTypes.image') },
    { value: 'video', label: t('templates.headerTypes.video') },
  ];
}

// ============================================================================
// API Helpers
// ============================================================================

const templateApi = {
  async list(businessId: string, t: (key: string) => string): Promise<{ templates: TemplateInfo[]; slugs: string[] }> {
    const res = await apiFetch(`/api/operator/templates?businessId=${encodeURIComponent(businessId)}`);
    if (!res.ok) throw new Error(t('templates.errors.loadFailed'));
    const json = await res.json();
    const data = json.data || json;
    const templates: TemplateInfo[] = (data.templates || []).map((t: Record<string, unknown>) => ({
      slug: t.slug as string,
      isCustom: !!t.isCustom,
      isDefault: t.isDefault !== false,
      category: (t.category as string) || null,
    }));
    return { templates, slugs: data.slugs || [] };
  },

  async get(slug: string, businessId: string, t: (key: string) => string): Promise<{ template: TemplateDetail; isCustom: boolean }> {
    const res = await apiFetch(`/api/operator/templates/${encodeURIComponent(slug)}?businessId=${encodeURIComponent(businessId)}`);
    if (!res.ok) throw new Error(t('templates.errors.loadTemplateFailed'));
    const json = await res.json();
    const data = json.data || json;
    return { template: data.template, isCustom: data.isCustom };
  },

  async save(
    slug: string,
    businessId: string,
    body: { bodyText: string; headerType?: string; headerValue?: string; footerText?: string; buttons?: unknown[]; category?: string },
    t: (key: string) => string,
  ): Promise<void> {
    const res = await apiFetch(`/api/operator/templates/${encodeURIComponent(slug)}?businessId=${encodeURIComponent(businessId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(t('templates.errors.saveFailed'));
  },

  async remove(slug: string, businessId: string, t: (key: string) => string): Promise<void> {
    const res = await apiFetch(`/api/operator/templates/${encodeURIComponent(slug)}?businessId=${encodeURIComponent(businessId)}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(t('templates.errors.restoreFailed'));
  },

  async preview(
    slug: string,
    businessId: string,
    body: { bodyText?: string; variables?: Record<string, string> },
    t: (key: string) => string,
  ): Promise<string> {
    const res = await apiFetch(`/api/operator/templates/${encodeURIComponent(slug)}/preview?businessId=${encodeURIComponent(businessId)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(t('templates.errors.previewFailed'));
    const json = await res.json();
    const data = json.data || json;
    return data.rendered;
  },
};

// ============================================================================
// WhatsApp Phone Preview — High-Fidelity Device Mockup
// ============================================================================

function renderWhatsAppMarkdown(text: string): string {
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  html = html.replace(/```([\s\S]*?)```/g, '<code style="background:#f0f0f0;padding:2px 5px;border-radius:3px;font-size:11.5px;font-family:\'SF Mono\',\'Menlo\',monospace">$1</code>');
  html = html.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
  html = html.replace(/_((?:[^_]|__)+)_/g, '<em>$1</em>');
  html = html.replace(/~([^~]+)~/g, '<s>$1</s>');
  html = html.replace(
    /\{\{(\w+)\}\}/g,
    '<span style="background:linear-gradient(135deg,#dbeafe,#e0f2fe);color:#0c4a6e;padding:1px 4px;border-radius:3px;font-size:11px;font-family:\'SF Mono\',monospace;border:1px solid #bae6fd">{{$1}}</span>',
  );
  html = html.replace(/\n/g, '<br />');

  return html;
}

// WhatsApp chat wallpaper — accurate doodle-style pattern
const WA_WALLPAPER_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect width='200' height='200' fill='%23eae6df'/%3E%3Cg fill='%23d6d1c4' fill-opacity='0.35'%3E%3Ccircle cx='25' cy='18' r='2'/%3E%3Ccircle cx='75' cy='42' r='1.5'/%3E%3Ccircle cx='140' cy='28' r='2'/%3E%3Ccircle cx='180' cy='65' r='1.5'/%3E%3Ccircle cx='45' cy='90' r='1.8'/%3E%3Ccircle cx='110' cy='78' r='1.5'/%3E%3Ccircle cx='160' cy='120' r='2'/%3E%3Ccircle cx='30' cy='140' r='1.5'/%3E%3Ccircle cx='85' cy='155' r='2'/%3E%3Ccircle cx='145' cy='170' r='1.5'/%3E%3Ccircle cx='55' cy='185' r='1.8'/%3E%3Ccircle cx='175' cy='190' r='1.5'/%3E%3Cpath d='M15 55 Q18 50 21 55 Q24 60 21 65 Q18 70 15 65Z' fill-opacity='0.2'/%3E%3Cpath d='M95 10 Q98 5 101 10 Q104 15 101 20 Q98 25 95 20Z' fill-opacity='0.2'/%3E%3Cpath d='M165 45 Q168 40 171 45 Q174 50 171 55 Q168 60 165 55Z' fill-opacity='0.2'/%3E%3Cpath d='M60 130 Q63 125 66 130 Q69 135 66 140 Q63 145 60 140Z' fill-opacity='0.2'/%3E%3Cpath d='M130 105l5-3 5 3-2 5h-6z' fill-opacity='0.18'/%3E%3Cpath d='M25 105l4-3 4 3-1.5 4h-5z' fill-opacity='0.18'/%3E%3Cpath d='M185 145l4-3 4 3-1.5 4h-5z' fill-opacity='0.18'/%3E%3Crect x='70' y='60' width='5' height='5' rx='1' fill-opacity='0.15'/%3E%3Crect x='150' y='88' width='4' height='4' rx='1' fill-opacity='0.15'/%3E%3Crect x='10' y='170' width='5' height='5' rx='1' fill-opacity='0.15'/%3E%3Crect x='120' y='150' width='4' height='4' rx='1' fill-opacity='0.15'/%3E%3C/g%3E%3C/svg%3E")`;

function WhatsAppPhonePreview({
  text,
  headerType,
  headerValue,
  footerText,
  quickReplies = [],
  urlButton,
  listSections = [],
  businessName,
}: {
  text: string;
  headerType?: string;
  headerValue?: string;
  footerText?: string;
  quickReplies?: QuickReply[];
  urlButton?: UrlButton | null;
  listSections?: Array<{ title: string; rows: Array<{ id: string; title: string; description?: string }> }>;
  businessName?: string;
}) {
  const { t, i18n } = useTranslation();
  const renderedHtml = useMemo(() => renderWhatsAppMarkdown(text || ''), [text]);
  const timeStr = new Date().toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
  const hasUrlButton = urlButton && urlButton.label.trim();
  const hasList = listSections.length > 0;
  const initial = (businessName || 'B').charAt(0).toUpperCase();

  return (
    <div className="flex flex-col items-center">
      {/* Device frame */}
      <div className="relative" style={{ width: 292, maxWidth: '100%', filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.18)) drop-shadow(0 8px 16px rgba(0,0,0,0.1))' }}>
        {/* Outer bezel */}
        <div className="relative overflow-hidden" style={{ borderRadius: 36, border: '2.5px solid #1a1a1a', background: 'linear-gradient(145deg, #2a2a2a 0%, #1a1a1a 50%, #0a0a0a 100%)', padding: 3 }}>
          <div className="relative overflow-hidden" style={{ borderRadius: 32, background: '#000' }}>
            {/* Screen reflection overlay */}
            <div className="absolute inset-0 pointer-events-none z-10" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 40%, transparent 60%, rgba(255,255,255,0.02) 100%)', borderRadius: 32 }} />
            {/* Dynamic island */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20">
              <div className="flex items-center justify-center" style={{ width: 72, height: 20, borderRadius: 12, backgroundColor: '#000' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'radial-gradient(circle, #1a1a2e 30%, #0d0d1a 100%)', border: '1px solid #2a2a3a' }} />
              </div>
            </div>

            {/* Status bar */}
            <div className="flex items-center justify-between px-6 pt-1 pb-0.5" style={{ backgroundColor: '#075e54', color: '#fff', fontSize: 11, fontWeight: 600, height: 36 }}>
              <span style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', letterSpacing: '-0.2px' }}>{timeStr}</span>
              <div className="flex items-center gap-[3px]">
                <svg width="14" height="11" viewBox="0 0 17 11"><rect x="0" y="8" width="3" height="3" rx="0.5" fill="white" /><rect x="4.5" y="5.5" width="3" height="5.5" rx="0.5" fill="white" /><rect x="9" y="3" width="3" height="8" rx="0.5" fill="white" /><rect x="13.5" y="0" width="3" height="11" rx="0.5" fill="white" /></svg>
                <svg width="13" height="10" viewBox="0 0 16 12" fill="white"><path d="M8 10.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" transform="translate(0,-2)" /><path d="M4.5 8.5C5.5 7 6.7 6.2 8 6.2s2.5.8 3.5 2.3" fill="none" stroke="white" strokeWidth="1.3" strokeLinecap="round" /><path d="M1.8 5.8C3.5 3.5 5.6 2.2 8 2.2s4.5 1.3 6.2 3.6" fill="none" stroke="white" strokeWidth="1.3" strokeLinecap="round" /></svg>
                <svg width="22" height="11" viewBox="0 0 28 13"><rect x="0" y="1" width="23" height="11" rx="2.5" fill="none" stroke="white" strokeWidth="1.2" /><rect x="2" y="3" width="17" height="7" rx="1" fill="white" /><rect x="24" y="4" width="2.5" height="5" rx="1" fill="white" opacity="0.5" /></svg>
              </div>
            </div>

            {/* WhatsApp header bar */}
            <div className="flex items-center gap-2 px-2.5 py-[7px]" style={{ backgroundColor: '#075e54' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              <div className="shrink-0 flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)', boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.2)', fontSize: 14, fontWeight: 700, color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                {initial}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate leading-tight" style={{ color: '#fff', fontSize: 15, fontWeight: 600, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>{businessName || t('templates.preview.businessFallback')}</p>
                <p className="leading-tight" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11.5, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>{t('templates.preview.online')}</p>
              </div>
              <div className="flex items-center gap-3.5">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round"><path d="M23 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 015.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 10.91a16 16 0 006 6l2.27-2.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0123 16.92z" /></svg>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" /></svg>
              </div>
            </div>

            {/* Chat area */}
            <div className="flex flex-col gap-[3px]" style={{ backgroundImage: WA_WALLPAPER_SVG, backgroundSize: '200px', minHeight: 220, padding: '10px 6px 8px', justifyContent: 'flex-end' }}>
              {/* Date chip */}
              <div className="flex justify-center mb-1">
                <span style={{ backgroundColor: 'rgba(255,255,255,0.92)', color: '#54656f', fontSize: 10.5, fontWeight: 500, padding: '3px 10px', borderRadius: 6, boxShadow: '0 1px 1px rgba(0,0,0,0.06)', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', textTransform: 'uppercase' as const, letterSpacing: '0.3px' }}>{t('templates.preview.today')}</span>
              </div>

              {/* Message bubble */}
              <div className="flex items-end max-w-[92%]" style={{ gap: 0 }}>
                <div className="shrink-0 self-start" style={{ width: 8, marginTop: 0 }}>
                  <svg width="8" height="13" viewBox="0 0 8 13"><path d="M5 0H8V2C8 2 6.5 2 5 4C3.5 6 2 9.5 0 13C0 8 1 4 2 2C3 0 5 0 5 0Z" fill="white" /></svg>
                </div>
                <div className="overflow-hidden" style={{ backgroundColor: '#fff', maxWidth: '100%', borderRadius: '0 8px 8px 8px', boxShadow: '0 1px 1px rgba(0,0,0,0.08)' }}>
                  {headerType === 'image' && headerValue && (
                    <div style={{ backgroundColor: '#dfe5e7', margin: 3, borderRadius: 5, overflow: 'hidden' }}>
                      <img src={headerValue} alt="" className="w-full object-cover" style={{ maxHeight: 130, display: 'block' }} onError={(e) => { const el = e.target as HTMLImageElement; el.style.display = 'none'; el.parentElement!.innerHTML = '<div style="height:70px;display:flex;align-items:center;justify-content:center;color:#8696a0;font-size:11px;gap:4px"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>Image</div>'; }} />
                    </div>
                  )}
                  {headerType === 'video' && (
                    <div className="flex items-center justify-center" style={{ backgroundColor: '#dfe5e7', height: 90, margin: 3, borderRadius: 5 }}>
                      <div className="flex flex-col items-center gap-1" style={{ color: '#8696a0', fontSize: 11, fontFamily: '-apple-system, sans-serif' }}>
                        <div className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.4)' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
                        </div>
                        <span>{t('templates.preview.video')}</span>
                      </div>
                    </div>
                  )}

                  <div style={{ padding: '5px 7px 3px', fontSize: 13.5, lineHeight: '19px', color: '#111b21', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                    {headerType === 'text' && headerValue && (
                      <div style={{ fontWeight: 600, fontSize: 13.5, color: '#111b21', marginBottom: 2 }}>{headerValue}</div>
                    )}
                    <div style={{ wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: renderedHtml }} />
                    <div className="flex items-end justify-between gap-3" style={{ marginTop: 1 }}>
                      {footerText ? <span style={{ fontSize: 11.5, color: '#8696a0', lineHeight: '16px' }}>{footerText}</span> : <span />}
                      <span className="shrink-0" style={{ fontSize: 10, color: '#8696a0', fontFamily: '-apple-system, sans-serif', lineHeight: '16px' }}>{timeStr}</span>
                    </div>
                  </div>

                  {/* In-bubble CTA / list buttons -- WhatsApp shows max 2 CTAs, then "See all options" */}
                  {(() => {
                    const ctaButtons: Array<{ type: 'url' | 'list'; label: string }> = [];
                    if (hasUrlButton) ctaButtons.push({ type: 'url', label: urlButton!.label });
                    if (hasList) ctaButtons.push({ type: 'list', label: t('templates.preview.seeOptions') });
                    const activeQR = quickReplies.filter((qr) => qr.title.trim());
                    const totalButtons = ctaButtons.length + activeQR.length;
                    const showOverflow = totalButtons > 3;
                    // WhatsApp rule: if >3 buttons, show first 2 CTAs then "See all options"
                    const visibleCTAs = showOverflow ? ctaButtons.slice(0, 2) : ctaButtons;
                    return (<>
                      {visibleCTAs.map((btn, idx) => (
                        <div key={idx} style={{ borderTop: '1px solid #e9edef' }}>
                          <button type="button" className="w-full flex items-center justify-center gap-1.5 py-[6px]" style={{ color: '#027eb5', fontSize: 13, fontWeight: 500, fontFamily: '-apple-system, sans-serif' }}>
                            {btn.type === 'url' ? (
                              <ExternalLink style={{ width: 14, height: 14 }} />
                            ) : (
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" /></svg>
                            )}
                            <span>{btn.label}</span>
                          </button>
                        </div>
                      ))}
                      {showOverflow && (
                        <div style={{ borderTop: '1px solid #e9edef' }}>
                          <button type="button" className="w-full flex items-center justify-center gap-1.5 py-[6px]" style={{ color: '#027eb5', fontSize: 13, fontWeight: 500, fontFamily: '-apple-system, sans-serif' }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" /></svg>
                            <span>{t('templates.preview.seeOptions')}</span>
                          </button>
                        </div>
                      )}
                    </>);
                  })()}
                </div>
              </div>

              {/* Quick reply chips -- WhatsApp shows these as rounded pills below the bubble with reply icon */}
              {(() => {
                const activeQR = quickReplies.filter((qr) => qr.title.trim());
                const ctaCount = (hasUrlButton ? 1 : 0) + (hasList ? 1 : 0);
                const totalButtons = ctaCount + activeQR.length;
                // If overflow (>3 total), quick replies are hidden in "See all options"
                if (activeQR.length === 0 || totalButtons > 3) return null;
                return (
                  <div className="flex flex-wrap gap-[5px] mt-[2px]" style={{ paddingLeft: 8 }}>
                    {activeQR.map((qr, idx) => (
                      <button key={idx} type="button" className="flex items-center justify-center gap-1" style={{ backgroundColor: '#fff', color: '#027eb5', fontSize: 12.5, fontWeight: 500, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', border: 'none', borderRadius: 18, padding: '5px 14px', boxShadow: '0 1px 1px rgba(0,0,0,0.08)', whiteSpace: 'nowrap' as const }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 00-4-4H4" /></svg>
                        <span>{qr.title}</span>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Bottom input bar */}
            <div className="flex items-center gap-[6px]" style={{ backgroundColor: '#f0f2f5', padding: '5px 6px' }}>
              <div className="flex items-center gap-2 shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#54656f" strokeWidth="1.6" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" strokeWidth="2.5" /><line x1="15" y1="9" x2="15.01" y2="9" strokeWidth="2.5" /></svg>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#54656f" strokeWidth="1.6" strokeLinecap="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" /></svg>
              </div>
              <div className="flex-1" style={{ backgroundColor: '#fff', borderRadius: 20, padding: '6px 12px', fontSize: 13, color: '#8696a0', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>{t('templates.preview.message')}</div>
              <div className="shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#54656f" strokeWidth="1.6" strokeLinecap="round"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>
              </div>
            </div>
            {/* Home indicator */}
            <div className="flex justify-center" style={{ backgroundColor: '#f0f2f5', paddingBottom: 6, paddingTop: 2 }}>
              <div style={{ width: 100, height: 4, borderRadius: 2, backgroundColor: '#1a1a1a' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Template Editor Dialog
// ============================================================================

function TemplateEditorDialog({
  open,
  onOpenChange,
  slug,
  businessId,
  businessName,
  onSaved,
  mode = 'edit',
  initialData,
  existingSlugs = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string | null;
  businessId: string;
  businessName?: string;
  onSaved: () => void;
  mode?: 'edit' | 'create';
  initialData?: { bodyText?: string; headerType?: string; headerValue?: string; footerText?: string; buttons?: Array<{ type: string; text: string; url?: string }>; category?: string };
  existingSlugs?: string[];
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCustom, setIsCustom] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Form state
  const [bodyText, setBodyText] = useState('');
  const [headerType, setHeaderType] = useState<string>('none');
  const [headerValue, setHeaderValue] = useState('');
  const [footerText, setFooterText] = useState('');
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);
  const [urlButton, setUrlButton] = useState<UrlButton | null>(null);
  const [newSlug, setNewSlug] = useState('');
  const [category, setCategory] = useState<string>('');
  const isCreateMode = mode === 'create';

  // Preview state
  const [previewText, setPreviewText] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Textarea ref for cursor positioning
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Load template when dialog opens
  useEffect(() => {
    if (!open) return;

    setError(null);
    setShowAdvanced(false);
    setSaveSuccess(false);

    // Create mode: use initialData or empty state
    if (isCreateMode) {
      setLoading(false);
      setIsCustom(false);
      setNewSlug(slug ? `${slug}_copy` : '');
      setCategory(initialData?.category || '');
      setBodyText(initialData?.bodyText || '');
      setHeaderType(initialData?.headerType || 'none');
      setHeaderValue(initialData?.headerValue || '');
      setFooterText(initialData?.footerText || '');
      setPreviewText(initialData?.bodyText || '');

      // Parse initialData buttons
      const qr: QuickReply[] = [];
      let ub: UrlButton | null = null;
      if (initialData?.buttons && Array.isArray(initialData.buttons)) {
        for (const btn of initialData.buttons) {
          if (btn.type === 'url' && btn.url) {
            ub = { label: btn.text || '', url: btn.url };
          } else {
            qr.push({ id: slugify(btn.text || ''), title: btn.text || '' });
          }
        }
      }
      setQuickReplies(qr);
      setUrlButton(ub);
      if (initialData?.headerType && initialData.headerType !== 'none' || initialData?.footerText) {
        setShowAdvanced(true);
      }
      return;
    }

    // Edit mode: fetch from API
    if (!slug) return;

    const controller = new AbortController();
    setLoading(true);

    templateApi
      .get(slug, businessId, t)
      .then(({ template, isCustom: custom }) => {
        if (controller.signal.aborted) return;
        setBodyText(template.bodyText || '');
        setHeaderType(template.headerType || 'none');
        setHeaderValue(template.headerValue || '');
        setFooterText(template.footerText || '');
        setIsCustom(custom);
        setPreviewText(template.bodyText || '');
        setCategory((template as unknown as Record<string, unknown>).category as string || '');

        // Parse existing buttons
        const qr: QuickReply[] = [];
        let ub: UrlButton | null = null;
        if (template.buttons && Array.isArray(template.buttons)) {
          for (const btn of template.buttons) {
            if (btn.type === 'url' && btn.url) {
              ub = { label: btn.text || '', url: btn.url };
            } else {
              qr.push({ id: slugify(btn.text || ''), title: btn.text || '' });
            }
          }
        }
        setQuickReplies(qr);
        setUrlButton(ub);

        const hasAdvancedContent =
          (template.headerType && template.headerType !== 'none') || !!template.footerText || qr.length > 0 || ub !== null;
        if (hasAdvancedContent) {
          setShowAdvanced(true);
        }
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : t('templates.errors.generic'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [open, slug, businessId, isCreateMode]);

  // Preview updates live via displayPreview = previewText || bodyText.
  // previewText is only set by the API "Preview" button (handlePreviewApi)
  // and on initial load. No debounce needed.

  const handleInsertVariable = useCallback(
    (variable: string) => {
      const textarea = textareaRef.current;
      if (!textarea) {
        setBodyText((prev) => prev + variable);
        return;
      }

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = bodyText.slice(0, start);
      const after = bodyText.slice(end);
      const newText = before + variable + after;
      setBodyText(newText);

      // Restore cursor position after React re-render
      requestAnimationFrame(() => {
        textarea.selectionStart = start + variable.length;
        textarea.selectionEnd = start + variable.length;
        textarea.focus();
      });
    },
    [bodyText],
  );

  const slugError = useMemo(() => {
    if (!isCreateMode) return null;
    if (!newSlug) return null;
    if (!/^[a-z][a-z0-9_]*$/.test(newSlug)) return t('templates.editor.slugInvalid');
    if (existingSlugs.includes(newSlug)) return t('templates.editor.slugDuplicate');
    return null;
  }, [isCreateMode, newSlug, existingSlugs, t]);

  const handleSave = useCallback(async () => {
    const saveSlug = isCreateMode ? newSlug : slug;
    if (!saveSlug) return;
    if (isCreateMode && slugError) return;
    setSaving(true);
    setError(null);

    try {
      // Serialize buttons into API format
      const buttons: Array<{ type: string; text: string; url?: string }> = [];
      for (const qr of quickReplies) {
        if (qr.title.trim()) {
          buttons.push({ type: 'quick_reply', text: qr.title.trim() });
        }
      }
      if (urlButton && urlButton.label.trim()) {
        buttons.push({ type: 'url', text: urlButton.label.trim(), url: urlButton.url.trim() });
      }

      await templateApi.save(saveSlug, businessId, {
        bodyText,
        headerType: headerType !== 'none' ? headerType : undefined,
        headerValue: headerType !== 'none' ? headerValue : undefined,
        footerText: footerText || undefined,
        buttons: buttons.length > 0 ? buttons : undefined,
        category: category || undefined,
      }, t);
      setSaveSuccess(true);
      onSaved();
      setTimeout(() => {
        setSaveSuccess(false);
        onOpenChange(false);
      }, 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templates.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [slug, newSlug, isCreateMode, slugError, businessId, bodyText, headerType, headerValue, footerText, quickReplies, urlButton, category, onSaved, onOpenChange, t]);

  const handlePreviewApi = useCallback(async () => {
    const previewSlug = isCreateMode ? (newSlug || 'preview') : slug;
    if (!previewSlug) return;
    setError(null);

    try {
      const rendered = await templateApi.preview(previewSlug, businessId, {
        bodyText,
        variables: {
          business_name: 'Meu Negocio',
          customer_name: 'Maria',
          service_name: 'Corte de Cabelo',
          date: new Date().toLocaleDateString('pt-BR'),
          time: '14:00',
          price: 'R$ 50,00',
        },
      }, t);
      setPreviewText(rendered);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templates.errors.previewFailed'));
    }
  }, [slug, newSlug, isCreateMode, businessId, bodyText, t]);

  const handleRestore = useCallback(async () => {
    if (!slug) return;
    setSaving(true);
    setError(null);

    try {
      await templateApi.remove(slug, businessId, t);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templates.errors.restoreFailed'));
    } finally {
      setSaving(false);
    }
  }, [slug, businessId, onSaved, onOpenChange, t]);

  const label = isCreateMode
    ? (slug ? t('templates.editor.cloneTitle') : t('templates.editor.createTitle'))
    : slug ? getSlugLabel(slug, t) : '';
  const isOverLimit = bodyText.length > BODY_MAX_LENGTH;
  const canSave = isCreateMode ? (!!newSlug && !slugError && !isOverLimit) : !isOverLimit;
  // Use API-rendered preview when available, otherwise live bodyText
  const displayPreview = previewText || bodyText;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 gap-0">
        {/* Branded header strip */}
        <div
          className="h-1 w-full rounded-t-lg"
          style={{ background: 'linear-gradient(90deg, #075e54 0%, #128c7e 40%, #25d366 100%)' }}
        />
        <DialogHeader className="px-6 pt-5 pb-4">
          <DialogTitle className="text-lg font-semibold tracking-tight">{label}</DialogTitle>
          <DialogDescription className="text-sm">
            {t('templates.editor.description')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-32 px-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 px-6 pb-2">
            {/* Editor panel */}
            <div className="space-y-4 pr-6 md:border-r border-border/50">
              {/* Slug input (create mode only) */}
              {isCreateMode && (
                <div className="space-y-2">
                  <Label htmlFor="new-slug" className="text-sm font-medium">{t('templates.editor.slugLabel')}</Label>
                  <Input
                    id="new-slug"
                    value={newSlug}
                    onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                    placeholder={t('templates.editor.slugPlaceholder')}
                    className={slugError ? 'border-red-500' : ''}
                  />
                  {slugError && <p className="text-xs text-red-500">{slugError}</p>}
                  <p className="text-xs text-muted-foreground">{t('templates.editor.slugHint')}</p>
                </div>
              )}

              {/* Category select (prominent in create mode) */}
              {isCreateMode && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t('templates.editor.categoryLabel')}</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('templates.editor.selectPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MARKETING">{t('templates.categories.marketing')}</SelectItem>
                      <SelectItem value="UTILITY">{t('templates.categories.utility')}</SelectItem>
                      <SelectItem value="AUTHENTICATION">{t('templates.categories.authentication')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="body-text" className="text-sm font-medium">{t('templates.editor.bodyLabel')}</Label>
                  <span
                    className={`text-xs tabular-nums ${isOverLimit ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}
                  >
                    {bodyText.length}/{BODY_MAX_LENGTH}
                  </span>
                </div>
                <div className="relative">
                  <Textarea
                    id="body-text"
                    ref={textareaRef}
                    value={bodyText}
                    onChange={(e) => setBodyText(e.target.value)}
                    placeholder={t('templates.editor.bodyPlaceholder')}
                    rows={10}
                    className={`font-mono text-sm leading-relaxed resize-none ${isOverLimit ? 'border-red-500 focus-visible:ring-red-500' : 'focus-visible:ring-[#128c7e]/30 focus-visible:border-[#128c7e]'}`}
                    style={{ boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.04)' }}
                  />
                  {/* Character count progress bar */}
                  <div className="h-1 w-full rounded-b-md overflow-hidden bg-muted/60 -mt-[1px] relative z-10">
                    <div
                      className="h-full rounded-b-md transition-all duration-300 ease-out"
                      style={{
                        width: `${Math.min((bodyText.length / BODY_MAX_LENGTH) * 100, 100)}%`,
                        background: isOverLimit
                          ? '#ef4444'
                          : bodyText.length / BODY_MAX_LENGTH > 0.8
                            ? '#f59e0b'
                            : 'linear-gradient(90deg, #128c7e, #25d366)',
                      }}
                    />
                  </div>
                </div>
                {isOverLimit && (
                  <p className="text-xs text-red-500">
                    {t('templates.editor.charLimitExceeded', { max: BODY_MAX_LENGTH })}
                  </p>
                )}
              </div>

              {/* Variable chips */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t('templates.editor.variablesLabel')}</Label>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_VARIABLES.map((v) => {
                    const varName = v.replace(/\{|\}/g, '');
                    return (
                      <button
                        key={v}
                        type="button"
                        onClick={() => handleInsertVariable(v)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#128c7e]/20 px-3 py-1 text-xs font-mono transition-all duration-150 hover:scale-105 hover:shadow-sm active:scale-100"
                        style={{
                          background: 'linear-gradient(135deg, rgba(37,211,102,0.06) 0%, rgba(18,140,126,0.1) 100%)',
                          color: '#075e54',
                        }}
                      >
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                          style={{
                            background: varName.includes('name') ? '#128c7e'
                              : varName.includes('date') || varName.includes('time') ? '#f59e0b'
                                : varName.includes('price') ? '#8b5cf6'
                                  : '#25d366',
                          }}
                        />
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Buttons section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">{t('templates.editor.buttonsLabel')}</Label>
                  {(quickReplies.length > 0 || urlButton) && (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {t('templates.editor.repliesCount', { count: quickReplies.length, max: MAX_QUICK_REPLIES })}
                      {urlButton ? ` · ${t('templates.editor.linkCount', { count: 1, max: MAX_URL_BUTTONS })}` : ''}
                    </span>
                  )}
                </div>

                {/* Quick reply rows -- card containers */}
                {quickReplies.map((qr, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 rounded-lg border bg-card p-2.5 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground/30 shrink-0 cursor-grab" />
                    <span className="text-xs font-medium text-muted-foreground w-4 shrink-0">{idx + 1}</span>
                    <Input
                      value={qr.title}
                      onChange={(e) => {
                        const updated = [...quickReplies];
                        updated[idx] = { id: slugify(e.target.value), title: e.target.value };
                        setQuickReplies(updated);
                      }}
                      placeholder={t('templates.editor.quickReplyPlaceholder')}
                      maxLength={BUTTON_TITLE_MAX}
                      className="flex-1 h-8 text-sm"
                    />
                    <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                      {qr.title.length}/{BUTTON_TITLE_MAX}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuickReplies(quickReplies.filter((_, i) => i !== idx))}
                      className="p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-950/20 text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}

                {/* URL button row -- card container */}
                {urlButton && (
                  <div className="rounded-lg border bg-card p-3 shadow-sm space-y-2.5">
                    <div className="flex items-center gap-2">
                      <div className="shrink-0 w-7 h-7 rounded-md bg-blue-50 dark:bg-blue-950/20 flex items-center justify-center">
                        <Link2 className="h-3.5 w-3.5 text-blue-500" />
                      </div>
                      <Input
                        value={urlButton.label}
                        onChange={(e) => setUrlButton({ ...urlButton, label: e.target.value })}
                        placeholder={t('templates.editor.linkButtonPlaceholder')}
                        maxLength={BUTTON_TITLE_MAX}
                        className="flex-1 h-8 text-sm"
                      />
                      <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                        {urlButton.label.length}/{BUTTON_TITLE_MAX}
                      </span>
                      <button
                        type="button"
                        onClick={() => setUrlButton(null)}
                        className="p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-950/20 text-muted-foreground hover:text-red-500 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <Input
                      value={urlButton.url}
                      onChange={(e) => setUrlButton({ ...urlButton, url: e.target.value })}
                      placeholder="https://..."
                      type="url"
                      className="h-8 text-sm text-muted-foreground"
                    />
                  </div>
                )}

                {/* Add buttons */}
                <div className="flex gap-2">
                  {quickReplies.length < MAX_QUICK_REPLIES && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setQuickReplies([...quickReplies, { id: '', title: '' }])}
                      className="h-8 text-xs border-dashed hover:border-solid hover:border-[#128c7e]/40 hover:text-[#075e54] transition-all"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {t('templates.editor.addQuickReply')}
                    </Button>
                  )}
                  {!urlButton && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setUrlButton({ label: '', url: '' })}
                      className="h-8 text-xs border-dashed hover:border-solid hover:border-blue-300 hover:text-blue-600 transition-all"
                    >
                      <Link2 className="h-3 w-3 mr-1" />
                      {t('templates.editor.addLinkButton')}
                    </Button>
                  )}
                </div>
              </div>

              <Separator className="my-1" />

              {/* Advanced options toggle */}
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full group rounded-md py-1.5 px-2 -mx-2 hover:bg-muted/50"
              >
                <ChevronDown
                  className="h-4 w-4 transition-transform duration-200"
                  style={{ transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
                <span className="flex-1 text-left">{t('templates.editor.advancedOptions')}</span>
                {/* Content indicator dots when collapsed */}
                {!showAdvanced && (headerType !== 'none' || footerText) && (
                  <span className="flex items-center gap-1">
                    {headerType !== 'none' && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#128c7e]" title="Header" />
                    )}
                    {footerText && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#25d366]" title="Footer" />
                    )}
                  </span>
                )}
              </button>

              {showAdvanced && (
                <div className="space-y-4 animate-in fade-in-0 slide-in-from-top-2 duration-300 pl-2 border-l-2 border-[#128c7e]/15 ml-0.5">
                  {/* Category (edit mode only — create mode shows above) */}
                  {!isCreateMode && (
                    <div className="space-y-2">
                      <Label>{t('templates.editor.categoryLabel')}</Label>
                      <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('templates.editor.selectPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('templates.categories.uncategorized')}</SelectItem>
                          <SelectItem value="MARKETING">{t('templates.categories.marketing')}</SelectItem>
                          <SelectItem value="UTILITY">{t('templates.categories.utility')}</SelectItem>
                          <SelectItem value="AUTHENTICATION">{t('templates.categories.authentication')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Header type */}
                  <div className="space-y-2">
                    <Label>{t('templates.editor.headerType')}</Label>
                    <Select value={headerType} onValueChange={setHeaderType}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('templates.editor.selectPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {getHeaderTypeOptions(t).map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Header value */}
                  {headerType !== 'none' && (
                    <div className="space-y-2">
                      <Label>
                        {headerType === 'text' && t('templates.editor.headerTextLabel')}
                        {headerType === 'image' && t('templates.editor.headerImageLabel')}
                        {headerType === 'video' && t('templates.editor.headerVideoLabel')}
                      </Label>
                      <Input
                        value={headerValue}
                        onChange={(e) => setHeaderValue(e.target.value)}
                        placeholder={headerType === 'text' ? t('templates.editor.headerPlaceholderText') : 'https://...'}
                        maxLength={headerType === 'text' ? HEADER_TEXT_MAX_LENGTH : undefined}
                      />
                      {headerType === 'text' && (
                        <span className="text-xs text-muted-foreground">{headerValue.length}/{HEADER_TEXT_MAX_LENGTH}</span>
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('templates.editor.footerLabel')}</Label>
                      <span className="text-xs text-muted-foreground">{footerText.length}/{FOOTER_MAX_LENGTH}</span>
                    </div>
                    <Input
                      value={footerText}
                      onChange={(e) => setFooterText(e.target.value)}
                      placeholder={t('templates.editor.footerPlaceholder')}
                      maxLength={FOOTER_MAX_LENGTH}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Preview panel */}
            <div className="space-y-3 pl-6">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground/70 font-medium">{t('templates.editor.previewLabel')}</Label>
              <div
                className="rounded-xl py-6 flex items-start justify-center min-h-[400px]"
                style={{
                  background: 'linear-gradient(145deg, #f8f9fa 0%, #f1f3f5 50%, #e9ecef 100%)',
                }}
              >
                {displayPreview ? (
                  <WhatsAppPhonePreview
                    text={displayPreview}
                    headerType={headerType}
                    headerValue={headerValue}
                    footerText={footerText}
                    quickReplies={quickReplies}
                    urlButton={urlButton}
                    businessName={businessName}
                  />
                ) : (
                  <div className="flex items-center justify-center h-64 text-sm text-muted-foreground/60">
                    {t('templates.editor.previewEmpty')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 px-4 py-3 mx-6 animate-in fade-in-0 slide-in-from-top-1 duration-200">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {saveSuccess && (
          <div className="rounded-md bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 px-4 py-3 mx-6 animate-in fade-in-0 slide-in-from-top-1 duration-200">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <p className="text-sm text-green-700 dark:text-green-400 font-medium">{t('templates.editor.saveSuccess') || 'Saved successfully'}</p>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2 px-6 pb-5 pt-3 border-t border-border/40">
          {!isCreateMode && isCustom && (
            <Button
              variant="ghost"
              onClick={handleRestore}
              disabled={saving}
              className="sm:mr-auto text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              {t('templates.editor.restoreDefault')}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handlePreviewApi}
            disabled={saving}
            className="transition-all"
          >
            <Eye className="mr-2 h-4 w-4" />
            {t('templates.editor.preview')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !canSave || saveSuccess}
            className="transition-all duration-200 min-w-[120px]"
            style={{
              background: saveSuccess
                ? '#22c55e'
                : 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)',
              color: '#fff',
              border: 'none',
            }}
          >
            {saveSuccess ? (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {t('templates.editor.saved') || 'Saved'}
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {saving ? t('templates.editor.saving') : t('templates.editor.save')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Main Templates Page
// ============================================================================

function TemplatesPage() {
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();

  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [slugs, setSlugs] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Editor dialog state
  const [editorSlug, setEditorSlug] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'edit' | 'create'>('edit');
  const [cloneData, setCloneData] = useState<{ bodyText?: string; headerType?: string; headerValue?: string; footerText?: string; buttons?: Array<{ type: string; text: string; url?: string }>; category?: string } | undefined>();

  // Category filter
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Delete confirmation
  const [deleteSlug, setDeleteSlug] = useState<string | null>(null);

  const fetchTemplates = useCallback(async (signal?: AbortSignal) => {
    if (!selectedBusinessId || !operator) return;
    setLoading(true);
    setError(null);
    setFetchError(null);

    try {
      const data = await templateApi.list(selectedBusinessId, t);
      if (signal?.aborted) return;
      setTemplates(data.templates);
      setSlugs(data.slugs);
    } catch (err) {
      if (signal?.aborted) return;
      const errorMessage = err instanceof Error ? err.message : t('templates.errors.loadFailed');
      setError(errorMessage);
      setFetchError(errorMessage);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [selectedBusinessId, operator, t]);

  useEffect(() => {
    const controller = new AbortController();
    fetchTemplates(controller.signal);
    return () => controller.abort();
  }, [fetchTemplates]);

  const handleOpenEditor = useCallback((slug: string) => {
    setEditorSlug(slug);
    setEditorMode('edit');
    setCloneData(undefined);
    setEditorOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setEditorSlug(null);
    setEditorMode('create');
    setCloneData(undefined);
    setEditorOpen(true);
  }, []);

  const handleClone = useCallback(async (slug: string) => {
    if (!selectedBusinessId) return;
    try {
      const { template } = await templateApi.get(slug, selectedBusinessId, t);
      setEditorSlug(slug);
      setEditorMode('create');
      setCloneData({
        bodyText: template.bodyText,
        headerType: template.headerType,
        headerValue: template.headerValue,
        footerText: template.footerText,
        buttons: template.buttons as Array<{ type: string; text: string; url?: string }>,
        category: (template as unknown as Record<string, unknown>).category as string | undefined,
      });
      setEditorOpen(true);
    } catch {
      // Silent fail — user can try again
    }
  }, [selectedBusinessId, t]);

  const handleDelete = useCallback(async () => {
    if (!deleteSlug || !selectedBusinessId) return;
    try {
      await templateApi.remove(deleteSlug, selectedBusinessId, t);
      setDeleteSlug(null);
      fetchTemplates();
    } catch {
      // Silent fail
    }
  }, [deleteSlug, selectedBusinessId, t, fetchTemplates]);

  // Filter templates by category
  const filteredTemplates = useMemo(() => {
    const baseList = templates.length > 0 ? templates : slugs.map((s) => ({ slug: s, isCustom: false, isDefault: true, category: null }));
    if (categoryFilter === 'all') return baseList;
    if (categoryFilter === 'uncategorized') return baseList.filter((t) => !t.category);
    return baseList.filter((t) => t.category === categoryFilter);
  }, [templates, slugs, categoryFilter]);

  const allSlugs = useMemo(() => templates.map((t) => t.slug), [templates]);

  // Loading state
  if (authLoading) {
    return <LoadingSpinner />;
  }

  if (!operator) return null;

  // No business selected
  if (!selectedBusinessId) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {t('templates.selectBusiness')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('templates.title')}</h1>
          <p className="text-muted-foreground">
            {selectedBusiness?.name && `${selectedBusiness.name} - `}
            {t('templates.subtitle')}
          </p>
        </div>
        <Button onClick={handleCreate} style={{ background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)', color: '#fff', border: 'none' }}>
          <Plus className="h-4 w-4 mr-2" />
          {t('templates.createTemplate')}
        </Button>
      </div>

      {/* Category filter */}
      <div className="flex items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('templates.allCategories')}</SelectItem>
            <SelectItem value="MARKETING">{t('templates.categories.marketing')}</SelectItem>
            <SelectItem value="UTILITY">{t('templates.categories.utility')}</SelectItem>
            <SelectItem value="AUTHENTICATION">{t('templates.categories.authentication')}</SelectItem>
            <SelectItem value="uncategorized">{t('templates.categories.uncategorized')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Fetch error banner */}
      {fetchError && (
        <div className="rounded-lg border border-destructive p-4 mb-4">
          <p className="text-destructive text-sm">{fetchError}</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <Card className="border-red-200 dark:border-red-800">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <Button variant="outline" size="sm" className="ml-auto" onClick={() => fetchTemplates()}>
              {t('templates.retry')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {loading ? (
        <LoadingSpinner />
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t('templates.noTemplatesFound')}</p>
          </CardContent>
        </Card>
      ) : (
        /* Template grid */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredTemplates.map((tmpl) => {
            const { slug, isCustom: isCustomTmpl, isDefault, category: tmplCategory } = tmpl;
            const label = getSlugLabel(slug, t);
            const IconComponent = SLUG_ICONS[slug] || MessageSquare;

            return (
              <Card
                key={slug}
                className="cursor-pointer hover:shadow-md hover:border-primary/50 transition-all duration-200 group relative"
                onClick={() => handleOpenEditor(slug)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="rounded-lg bg-muted p-2 group-hover:bg-primary/10 transition-colors">
                      <IconComponent className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      {tmplCategory && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {t(`templates.categories.${tmplCategory.toLowerCase()}`)}
                        </Badge>
                      )}
                      <Badge
                        variant={isCustomTmpl ? 'default' : 'secondary'}
                        className={
                          isCustomTmpl
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-100'
                            : ''
                        }
                      >
                        {isCustomTmpl ? t('templates.customized') : t('templates.default')}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardTitle className="text-sm font-medium leading-snug">{label}</CardTitle>
                  {/* Clone & Delete actions */}
                  <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                      onClick={(e) => { e.stopPropagation(); handleClone(slug); }}
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      {t('templates.clone')}
                    </Button>
                    {isCustomTmpl && !isDefault && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-muted-foreground hover:text-red-600"
                        onClick={(e) => { e.stopPropagation(); setDeleteSlug(slug); }}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        {t('templates.deleteTemplate')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteSlug} onOpenChange={(open) => !open && setDeleteSlug(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('templates.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('templates.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Editor dialog */}
      {selectedBusinessId && operator && (
        <TemplateEditorDialog
          open={editorOpen}
          onOpenChange={setEditorOpen}
          slug={editorSlug}
          businessId={selectedBusinessId}
          businessName={selectedBusiness?.name}
          onSaved={fetchTemplates}
          mode={editorMode}
          initialData={cloneData}
          existingSlugs={allSlugs}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/templates')({
  component: TemplatesPage,
});
